import { Hub, HubCallback } from '@aws-amplify/core'
import router, { NextRouter } from 'next/router'
import { useEffect, useState, VFC } from 'react'
import { signInWithCognito } from 'src/api/auth'
import { PlaneIcon } from 'src/assets/svgs'
import { GuestPageWrapper } from 'src/components/PageWrapper'
import { AuthEvent } from 'src/external/aws/cognito'
import { useMessageModal } from 'src/hooks/useModal'
import { AuthService } from 'src/services/AuthService'
import { MYPAGE } from 'src/utils/routes'
import { isString } from 'src/utils/typeguard'
import { ResetPasswordForm } from './Forms/ResetPassword'
import { SignIn } from './Forms/SignIn'

type SignInFormData = {
  email: string
  password: string
}
type AuthProps = {
  router: {
    query: NextRouter['query']
  }
}
export const Auth: VFC<AuthProps> = ({ router: { query } }) => {
  const [submitNewPassword, setSubmitNewPassword] =
    useState<(password: string) => Promise<any>>()
  const [hasSocialLoginFailed, setHasSocialLoginFailed] = useState(false)

  const { open: openPasswordResetModal } = useMessageModal()

  const submitSignIn = (data: SignInFormData) =>
    AuthService.signIn(data, {
      onSuccess: () => router.push(MYPAGE),
      onPasswordResetRequired: () =>
        openPasswordResetModal({
          title: 'パスワードの再設定が必要です',
          content: <PlaneIcon width="160px" height="140px" />,
          message: `${data.email}宛にパスワード再設定のEメールを送信しました。メールに記載されたURLをクリックしてパスワードの再設定を行ってください。\n\n今から15分以内にメールが届かない場合、入力されたメールアドレスが正しいことを確認して、迷惑メールフォルダをご確認ください。`,
        }),
      onNewPasswordRequired: (cb) => setSubmitNewPassword(() => cb),
    })

  useEffect(() => {
    const { email, verificationCode } = query
    if (isString(email) && isString(verificationCode)) {
      setSubmitNewPassword(
        () => (newPassword: string) =>
          AuthService.resetPassword({
            email,
            verificationCode,
            newPassword,
          }).then(() => submitSignIn({ email, password: newPassword })),
      )
    }
  }, [query])
  useEffect(() => {
    const authCallback: HubCallback = ({ payload }: { payload: AuthEvent }) => {
      switch (payload.event) {
        case 'configured':
          AuthService.idToken()
            .then(signInWithCognito)
            .then(() => router.push(MYPAGE))
          return
        case 'signIn_failure':
          setHasSocialLoginFailed(payload.data?.message.startsWith('PreSignUp'))
          return
      }
    }
    Hub.listen('auth', authCallback)
    AuthService.initialize()
    return () => Hub.remove('auth', authCallback)
  }, [])

  return (
    <GuestPageWrapper>
      {submitNewPassword ? (
        <ResetPasswordForm submit={submitNewPassword} />
      ) : (
        <SignIn
          submit={submitSignIn}
          hasSocialLoginFailed={hasSocialLoginFailed}
        />
      )}
    </GuestPageWrapper>
  )
}
